import { createFileRoute, useNavigate, useParams } from '@tanstack/react-router';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { PopupEditor } from '@acorn/popup-editor';
import type { PopupVersionEntry } from '@acorn/popup-editor';
import type { PopupDocument } from '@acorn/popup-core';
import { popupApi } from '@/lib/api-client';
import { toast } from 'sonner';
import { useCallback, useState } from 'react';

export const Route = createFileRoute('/popups/$popupId/edit')({
  component: PopupEditorPage,
});

function PopupEditorPage() {
  const { popupId } = useParams({ from: '/popups/$popupId/edit' });
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [publishing, setPublishing] = useState(false);

  const { data: popup, isLoading, error } = useQuery({
    queryKey: ['popups', popupId],
    queryFn: () => popupApi.get(popupId),
    enabled: !!popupId,
  });

  const updatePopup = useMutation({
    mutationFn: (doc: PopupDocument) => popupApi.update(popupId, { document: doc }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['popups'] }),
  });

  const { data: versionsData } = useQuery({
    queryKey: ['popups', popupId, 'versions'],
    queryFn: () => popupApi.listVersions(popupId),
    enabled: !!popupId,
  });

  const restoreVersion = useMutation({
    mutationFn: (version: number) => popupApi.restoreVersion(popupId, version),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['popups'] }),
  });

  const versions: PopupVersionEntry[] = (versionsData?.items ?? []).map((v) => ({
    version: v.version,
    created_at: v.created_at,
    change_summary: v.change_summary,
  }));

  const handleRestore = async (version: number) => {
    try {
      await restoreVersion.mutateAsync(version);
      toast.success(`Restored to version ${version}`);
      window.location.reload();
    } catch {
      toast.error('Failed to restore version');
    }
  };

  const handleSave = useCallback(async (doc: PopupDocument) => {
    await updatePopup.mutateAsync(doc);
  }, [updatePopup]);

  const handleClose = useCallback(() => {
    navigate({ to: '/' });
  }, [navigate]);

  const handlePublish = async () => {
    setPublishing(true);
    try {
      const result = await popupApi.publish(popupId);
      queryClient.invalidateQueries({ queryKey: ['popups'] });
      toast.success(result.message || 'Published');
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Failed to publish');
    } finally {
      setPublishing(false);
    }
  };

  const handleUnpublish = async () => {
    setPublishing(true);
    try {
      const result = await popupApi.unpublish(popupId);
      queryClient.invalidateQueries({ queryKey: ['popups'] });
      toast.success(result.message || 'Unpublished');
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Failed to unpublish');
    } finally {
      setPublishing(false);
    }
  };

  if (isLoading) {
    return (
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100vh', color: '#6b7280' }}>
        Loading editor...
      </div>
    );
  }

  if (error || !popup) {
    return (
      <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', height: '100vh', gap: 16 }}>
        <h2 style={{ fontSize: 18, fontWeight: 600, color: '#991b1b' }}>Popup not found</h2>
        <button
          onClick={handleClose}
          style={{ padding: '8px 16px', border: '1px solid #d1d5db', borderRadius: 8, background: 'white', cursor: 'pointer' }}
        >
          Back to Popups
        </button>
      </div>
    );
  }

  const isActive = popup.status === 'active';

  return (
    <PopupEditor
      initialDocument={popup.document}
      onSave={handleSave}
      onClose={handleClose}
      onPublish={handlePublish}
      onUnpublish={handleUnpublish}
      isPublished={isActive}
      isPublishing={publishing}
      status={popup.status as 'draft' | 'active' | 'paused' | 'archived'}
      publishedVersion={popup.published_version}
      versions={versions}
      onRestoreVersion={handleRestore}
      isRestoring={restoreVersion.isPending}
    />
  );
}
